// src/pages/admin/components/sections/navsection/NavSection1.jsx
import React, { useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { pagesApi } from "../../../../api/pagesApi";

function readStoredUser() {
  try {
    const raw = localStorage.getItem("sb_user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function readManagerSlug() {
  try {
    return String(localStorage.getItem("sb_manager_slug") || "").trim();
  } catch {
    return "";
  }
}

const safeArray = (v) => (Array.isArray(v) ? v : []);
const isExternalUrl = (href) => /^https?:\/\//i.test(href);

const replaceSlug = (href, slug) => {
  const s = String(slug || "").trim();
  if (!href) return "";
  return String(href).split("{slug}").join(s);
};

// alignment value is physical: left means left of screen
const pickAlign = (config, direction) => {
  const raw = String(config?.alignment || "").toLowerCase().trim();
  if (raw === "left" || raw === "center" || raw === "right") return raw;
  return direction === "rtl" ? "right" : "left";
};

export default function NavSection1({ config = {}, style = {}, general = {}, pageSlug = "" }) {
  const navigate = useNavigate();
  const location = useLocation();

  const direction = (config?.direction || "ltr").toLowerCase();
  const rtl = direction === "rtl";

  const align = pickAlign(config, direction);

  // physical align mapping (works correctly in RTL)
  const justifyLinks =
    align === "center"
      ? "justify-center"
      : align === "left"
      ? rtl
        ? "justify-end"
        : "justify-start"
      : rtl
      ? "justify-start"
      : "justify-end";

  const mobileTextAlign =
    align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left";

  const [mobileOpen, setMobileOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const enabled = config?.enabled !== false;
  const sticky = !!config?.sticky;

  const bgColor = style?.bgColor || "#0B1220";
  const textColor = style?.textColor || "#ffffff";
  const linkColor = style?.linkColor || "rgba(255,255,255,0.85)";
  const buttonBgColor = style?.buttonBgColor || "#ffffff";
  const buttonTextColor = style?.buttonTextColor || "#0B1220";
  const borderColor = style?.borderColor || "rgba(255,255,255,0.12)";

  const brandName = config?.brandName || "My Business";
  const brandLogo = config?.brandLogo || general?.logoUrl || "";

  const links = useMemo(() => safeArray(config.links), [config.links]);

  const showLogin = (config?.showLogin ?? config?.showManagerLogin) !== false;
  const loginButtonText = config?.loginButtonText || config?.managerButtonText || "Login";

  // edit target
  const managerTargetRoute =
    config?.managerTargetRoute ||
    config?.editTargetRoute ||
    "/{slug}/manager";

  const editButtonText = config?.editButtonText || "Edit";
  const showEditButton = (config?.showEditButton ?? true) !== false;

  // If pageSlug is missing for any reason, try to infer from URL
  const inferredSlug = useMemo(() => {
    const parts = String(location.pathname || "").split("/").filter(Boolean);
    return parts[0] || "";
  }, [location.pathname]);

  const effectiveSlug = String(pageSlug || inferredSlug || "").trim();

  const storedUser = readStoredUser();
  const storedToken = localStorage.getItem("sb_token");

  // manager session is valid ONLY on its own slug
  const managerSlug = String(storedUser?.pageSlug || readManagerSlug() || "").trim();
  const isManagerLoggedIn =
    !!storedToken &&
    storedUser?.role === "manager" &&
    managerSlug === String(effectiveSlug || "").trim();

  const homePath = useMemo(() => {
    return effectiveSlug ? `/${effectiveSlug}` : "/";
  }, [effectiveSlug]);

  const managerEditorPath = useMemo(() => {
    const target = replaceSlug(managerTargetRoute, effectiveSlug).trim();
    return target || (effectiveSlug ? `/${effectiveSlug}/manager` : "/");
  }, [managerTargetRoute, effectiveSlug]);

  if (!enabled) return null;

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const goHome = () => {
    setMobileOpen(false);

    if (location.pathname === homePath) {
      scrollToTop();
      return;
    }

    navigate(homePath);
    setTimeout(() => scrollToTop(), 50);
  };

  const go = (hrefRaw) => {
    const href = replaceSlug(hrefRaw, effectiveSlug).trim();
    if (!href) return;

    if (href === homePath || href === "/" || href === `/${effectiveSlug}`) {
      goHome();
      return;
    }

    if (href.startsWith("#")) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      setMobileOpen(false);
      return;
    }

    if (isExternalUrl(href)) {
      window.open(href, "_blank", "noopener,noreferrer");
      setMobileOpen(false);
      return;
    }

    navigate(href);
    setMobileOpen(false);
  };

  // Hard redirect so RoleRoute/AuthProvider picks token
  const goEdit = () => {
    setMobileOpen(false);
    window.location.assign(managerEditorPath);
  };

  const openLogin = () => {
    setError("");
    setEmail("");
    setPassword("");
    setLoginOpen(true);
    setMobileOpen(false);
  };

  const logout = () => {
    localStorage.removeItem("sb_token");
    localStorage.removeItem("sb_user");
    localStorage.removeItem("sb_manager_slug");
    window.location.assign(homePath);
  };

  //  manager login must include slug, server will enforce it
  const submitLogin = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Email and password required");
      return;
    }

    try {
      setBusy(true);

      const data = await pagesApi.login({ email, password, slug: effectiveSlug });

      if (data?.user?.role !== "manager") {
        setError("This account is not a manager.");
        return;
      }

      //  bind manager session to this slug (UI)
      const userWithSlug = { ...(data.user || {}), pageSlug: effectiveSlug };

      localStorage.setItem("sb_token", data.token);
      localStorage.setItem("sb_user", JSON.stringify(userWithSlug));
      localStorage.setItem("sb_manager_slug", effectiveSlug);

      setLoginOpen(false);
      setPassword("");

      // reload on the business page
      window.location.assign(homePath);
    } catch (err) {
      setError(err?.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div
        dir={direction}
        className={`${sticky ? "sticky top-0" : ""} z-50`}
        style={{ backgroundColor: bgColor, borderBottom: `1px solid ${borderColor}` }}
      >
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          {/* Brand */}
          <button type="button" onClick={goHome} className="flex items-center gap-3 select-none">
            {brandLogo ? (
              <img
                src={brandLogo}
                alt="Brand"
                className="w-9 h-9 rounded-xl object-cover"
                style={{ border: `1px solid ${borderColor}` }}
              />
            ) : (
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold"
                style={{ color: textColor, border: `1px solid ${borderColor}` }}
              >
                {brandName?.slice(0, 1)?.toUpperCase() || "B"}
              </div>
            )}

            <div className="font-bold truncate" style={{ color: textColor }}>
              {brandName}
            </div>
          </button>

          {/* Desktop links */}
          <div className={`hidden md:flex flex-1 ${justifyLinks} ps-4`}>
            <div className="flex items-center gap-6">
              {(links.length ? links : [{ label: "Home", href: "/{slug}" }]).map((l, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => go(l?.href)}
                  className="text-sm font-medium hover:opacity-90"
                  style={{ color: linkColor }}
                >
                  {l?.label || ""}
                </button>
              ))}
            </div>
          </div>

          {/* Desktop manager actions */}
          <div className="hidden md:flex items-center ms-auto flex-shrink-0 gap-2">
            {showLogin ? (
              isManagerLoggedIn ? (
                <>
                  {showEditButton ? (
                    <button
                      onClick={goEdit}
                      className="px-4 py-2 rounded-lg text-sm font-medium"
                      style={{ backgroundColor: buttonBgColor, color: buttonTextColor }}
                    >
                      {editButtonText}
                    </button>
                  ) : null}

                  <button
                    onClick={logout}
                    className="px-4 py-2 rounded-lg text-sm font-medium border"
                    style={{ borderColor, color: textColor }}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <button
                  onClick={openLogin}
                  className="px-4 py-2 rounded-lg text-sm font-medium"
                  style={{ backgroundColor: buttonBgColor, color: buttonTextColor }}
                >
                  {loginButtonText}
                </button>
              )
            ) : null}
          </div>

          {/* Mobile toggle */}
          <button
            className="md:hidden ms-auto px-3 py-2 rounded-lg"
            style={{ border: `1px solid ${borderColor}`, color: textColor }}
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? "Close" : "Menu"}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen ? (
          <div className="md:hidden px-4 pb-4">
            <div className="rounded-2xl p-3" style={{ border: `1px solid ${borderColor}` }}>
              <div className="flex flex-col gap-2">
                {(links.length ? links : [{ label: "Home", href: "/{slug}" }]).map((l, idx) => (
                  <button
                    key={idx}
                    onClick={() => go(l?.href)}
                    className={`px-3 py-2 rounded-lg hover:bg-white/10 text-sm ${mobileTextAlign}`}
                    style={{ color: linkColor }}
                  >
                    {l?.label || ""}
                  </button>
                ))}

                {showLogin ? (
                  isManagerLoggedIn ? (
                    <>
                      {showEditButton ? (
                        <button
                          onClick={goEdit}
                          className="px-4 py-2 rounded-lg text-sm font-medium"
                          style={{ backgroundColor: buttonBgColor, color: buttonTextColor }}
                        >
                          {editButtonText}
                        </button>
                      ) : null}

                      <button
                        onClick={logout}
                        className="px-4 py-2 rounded-lg text-sm font-medium border"
                        style={{ borderColor, color: textColor }}
                      >
                        Logout
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={openLogin}
                      className="px-4 py-2 rounded-lg text-sm font-medium"
                      style={{ backgroundColor: buttonBgColor, color: buttonTextColor }}
                    >
                      {loginButtonText}
                    </button>
                  )
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Login Modal */}
      {loginOpen ? (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden border">
            <div className="p-5 border-b flex items-center justify-between">
              <div>
                <div className="text-lg font-bold text-gray-900">Manager Login</div>
                <div className="text-sm text-gray-500">Login with the manager account created by the admin.</div>
              </div>
              <button
                onClick={() => !busy && setLoginOpen(false)}
                className="text-2xl text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <form onSubmit={submitLogin} className="p-5 space-y-4">
              <div>
                <div className="text-sm font-medium text-gray-700 mb-1">Email</div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="manager@example.com"
                  required
                  disabled={busy}
                />
              </div>

              <div>
                <div className="text-sm font-medium text-gray-700 mb-1">Password</div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="••••••••"
                  required
                  disabled={busy}
                />
              </div>

              {error ? <div className="text-sm text-red-600">{error}</div> : null}

              <div className="flex gap-3 justify-end pt-1">
                <button
                  type="button"
                  onClick={() => setLoginOpen(false)}
                  className="px-4 py-2 rounded-lg border hover:bg-gray-50 disabled:opacity-50"
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-black disabled:opacity-50"
                  disabled={busy}
                >
                  {busy ? "Logging in..." : "Login"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}